/**
 * Why THIS tool is being suggested to HER (Revital item 28, Danna 22.09).
 *
 * The recommendation strip used to carry icons and one general sentence, which
 * says nothing about the woman looking at it. A reason has to come from her own
 * answers, so it is derived from the symptoms she actually rated: the highest
 * one she reported picks a group, and the tools in that group can say the
 * reason out loud ("because you rated broken sleep high").
 *
 * When there is no rating to point at there is NO reason line. A tool never
 * invents one.
 */

/** Symptom groups, mirroring the results-side domains so both tell one story. */
const SYMPTOM_GROUPS: { key: string; ids: string[]; tools: string[] }[] = [
  {
    key: 'sleep',
    ids: [
      'insomnia_falling_asleep',
      'insomnia_staying_asleep',
      'early_awakening',
      'night_sweats',
      'unrefreshing_sleep',
      'daytime_sleepiness',
    ],
    tools: ['sleepex', 'sleep', 'tracker', 'meditate', 'breathe'],
  },
  {
    key: 'mood',
    ids: [
      'mood_swings',
      'anxiety',
      'low_mood',
      'irritability',
      'stress_sensitivity',
    ],
    tools: ['voice', 'mind', 'meditate', 'next', 'breathe'],
  },
  {
    key: 'cognition',
    ids: [
      'brain_fog',
      'forgetfulness',
      'difficulty_concentrating',
      'slow_recall',
    ],
    tools: ['brain', 'myth', 'meditate', 'mind'],
  },
  {
    key: 'body',
    ids: ['energy_dips', 'motivation_drop', 'weight_gain', 'hot_flashes'],
    tools: ['water', 'meal', 'supps', 'bone', 'steps', 'kegel'],
  },
];

/** What the rated-symptoms endpoint actually returns: a sorted key/value list. */
export interface RatedSymptom {
  key: string;
  value: number;
}

/**
 * The symptom she rated highest, above zero. Null when she rated nothing.
 * Accepts the endpoint's list as well as a plain map, so a shape change on
 * either side cannot silently blank the reason line.
 */
export function topRatedSymptom(
  ratings: RatedSymptom[] | Record<string, number> | null | undefined,
): { id: string; value: number } | null {
  if (!ratings) return null;
  const pairs: [string, unknown][] = Array.isArray(ratings)
    ? ratings.map((r) => [r?.key, r?.value] as [string, unknown])
    : Object.entries(ratings);

  let best: { id: string; value: number } | null = null;
  for (const [id, raw] of pairs) {
    if (typeof id !== 'string' || !id) continue;
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) continue;
    if (!best || value > best.value) best = { id, value };
  }
  return best;
}

/**
 * The tool ids that speak to her top symptom. Empty when nothing matches, so
 * the caller shows no reason rather than a vague one.
 */
export function toolsForSymptom(symptomId: string): string[] {
  const group = SYMPTOM_GROUPS.find((g) => g.ids.includes(symptomId));
  return group ? group.tools : [];
}
